import { Component, OnDestroy, OnInit } from '@angular/core';

import { Subject, Subscription } from 'rxjs';

import { AuthService } from '../../../../providers/auth.service';
import { SharedClientService } from '../../shared/services/shared-client.service';
import { CreditCardStateService } from '../credit-card/store/credit-card-state.service';
import { PackageStateService } from './store/package-state.service';
import { SubscriptionStateService } from '../subscription/store/subscription-state.service';
import { NotificationService } from '../../../shared/notification/notification.service';
import { BillingStateService } from '../store/billing-state.service';

import { Package } from './store/package';
import { PackageState } from './store/package-state';
import { SubscriptionState } from '../subscription/store/subscription-state';
import { BillingState } from '../store/billing-state';

import * as REASONS from './store/not-allowed-reasons';


export class UpgradeOrDowngradeComponent implements OnInit, OnDestroy {

	public state: PackageState;
	public billingState: BillingState;
	public actionInProgress = false;
	public readonly REASONS = REASONS;

	private unsubscribe$ = new Subject();
	private shouldNotReloaded: boolean;

	private previousCurrentBillingState: SubscriptionState;

	constructor(
		private authService: AuthService,
		private billingStateService: BillingStateService,
		private notificationService: NotificationService,
		private clientService: SharedClientService,
		private creditCardStateService: CreditCardStateService,
		private packageService: PackageStateService,
		private subscriptionStateService: SubscriptionStateService) { }

	public ngOnInit(): void {
		this.subscribeToBillingState();
		this.subscribeToState();
		this.subscribeToCreditCardState();
		this.subscribeToSubscriptionState();

		this.getPackages();
	}

	public ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	public onPackageActionClick(pac: Package) {
		if (!pac.subscribe_action_allowed || this.billingState.actionInProgress) {
			return;
		}

		const data = {
			package: pac.identifier,
			products: pac.template_products.filter((prod) => prod.plan.selected_credit_id).map((product) => {
				const selectedCredit = product.plan.credits.find((credit) => credit.id === product.plan.selected_credit_id);
				return {
					product_identifier: product.identifier,
					credits: selectedCredit.credits
				};
			})
		};

		if (!data.products.length) {
			return;
		}

		this.actionInProgress = true;
		this.billingStateService.setActionInProgress(true);
		if (pac.subscribe_action === 'downgrade') {
			this.clientService.downgradeClientBillingPackage(String(this.authService.userId), data).subscribe(
				() => this.afterDowngradeOrUpgradeComplete(),
				() => { this.actionInProgress = false; this.billingStateService.setActionInProgress(false); }
			);
		} else {
			this.clientService.upgradeClientBillingPackage(String(this.authService.userId), data).subscribe(
				() => this.afterDowngradeOrUpgradeComplete(),
				() => { this.actionInProgress = false; this.billingStateService.setActionInProgress(false); }
			);
		}
	}

	public onGetInTouchClick() {
		this.clientService.emitEventAction({action: 'open-client-aside-help', pyload: null});
	}

	public onOfferCreditChange(pac: Package) {
		pac.price = this.getPackagePrice(pac);
	}

	public reloadData(): void {
		this.packageService.resetFirstLoad();
		this.getPackages();
	}

	private subscribeToCreditCardState() {
		this.creditCardStateService.state$.takeUntil(this.unsubscribe$).subscribe(
			(creditCardState) => {
				if (creditCardState.firstLoad && !creditCardState.loading) {
					this.getPackages();
				}
			}
		);
	}

	private subscribeToSubscriptionState() {
		this.subscriptionStateService.state$.takeUntil(this.unsubscribe$).subscribe(
			(subscriptionState) => {
				if (!subscriptionState.loading) {
					if (this.checkIfShouldReload(subscriptionState)) {
						this.getPackages();
					}
					this.previousCurrentBillingState = subscriptionState;
				}
			}
		);
	}

	private subscribeToState() {
		this.packageService.state$.takeUntil(this.unsubscribe$).subscribe(
			(state) => {
				this.state = state;
			}
		);
	}

	private subscribeToBillingState() {
		this.billingStateService.state$.takeUntil(this.unsubscribe$).subscribe(
			(state) => {
				this.billingState = state;
			}
		);
	}

	private getPackages() {
		this.packageService.setLoading(true);
		this.clientService.getClientBillingPackages(String(this.authService.userId)).subscribe(
			(packages) => {
				const pacs =  packages.map((pac) => {
					return {
						...pac,
						price: this.getPackagePrice(pac)
					};
				});

				this.packageService.setEntries(pacs);
			}
		);
	}

	private afterDowngradeOrUpgradeComplete() {
		this.actionInProgress = false;
		this.shouldNotReloaded = true;
		this.getPackages();
		this.notificationService.emitEventAction({action: 'reload-notifications'});
		this.clientService.emitEventAction('reloadClientPermissions');
		this.billingStateService.setActionInProgress(false);
	}

	private getPackagePrice(pac: Package) {
		let newPrice = 0;
		pac['priceWithoutDiscount'] = 0;
		pac.template_products.forEach((offer) => {
			const selectedCredit = offer.plan.credits.find((credit) => credit.id === offer.plan.selected_credit_id);
			if (selectedCredit) {
				newPrice += +selectedCredit.price;
			}
		});
		pac['priceWithoutDiscount'] = newPrice;

		if (pac.discount) {
			if (pac.discount > newPrice) {
				return 0;
			}

			newPrice -= pac.discount;
		}

		return newPrice;
	}

	private checkIfShouldReload(subscriptionState: SubscriptionState): boolean {
		if (!this.previousCurrentBillingState || this.shouldNotReloaded) {
			this.shouldNotReloaded = false;
			return false;
		}

		if ((!subscriptionState.currentBilling && this.previousCurrentBillingState.currentBilling) || (subscriptionState.currentBilling && this.previousCurrentBillingState &&
			this.previousCurrentBillingState.currentBilling && subscriptionState.currentBilling.cancel_at_period_end !== this.previousCurrentBillingState.currentBilling.cancel_at_period_end)) {
				return true;
		}
		if ((!subscriptionState.upcomingBilling && this.previousCurrentBillingState.upcomingBilling) || (subscriptionState.upcomingBilling && this.previousCurrentBillingState &&
			this.previousCurrentBillingState.upcomingBilling && subscriptionState.upcomingBilling.cancel_at_period_end !== this.previousCurrentBillingState.upcomingBilling.cancel_at_period_end)) {
			return true;
		}

		return false;
	}
static ctorParameters() { return [ { type: AuthService }, { type: BillingStateService }, { type: NotificationService }, { type: SharedClientService }, { type: CreditCardStateService }, { type: PackageStateService }, { type: SubscriptionStateService } ]; }}



// WEBPACK FOOTER //
// ./node_modules/ngc-webpack??ref--0-2!./node_modules/angular2-template-loader!./src/app/components/client/billing/upgrade-or-downgrade/upgrade-or-downgrade.component.ts